// ClientFormFisaServicePdfTemplate.js
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { styles as base } from "./styles/PdfStyles";
import hardcodedLogo from "../../assets/img/hardcoded-logos/mc-business.jpg";

const safe = (v) => (v == null ? "" : String(v));

const hasValue = (arr, value) => {
  if (!Array.isArray(arr)) return false;
  return arr.some((x) => (typeof x === "string" ? x === value : x?.value === value));
};

const altChecked = (arr, altText) =>
  hasValue(arr, "alta") || (altText && String(altText).trim().length > 0);

const isSafeImageSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  return (
    src.startsWith("data:image/") ||
    src.startsWith("/") ||
    src.startsWith("http://localhost") ||
    src.startsWith("https://localhost")
  );
};

function Checkbox({ checked, label }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 6 }}>
      <View
        style={{
          width: 12,
          height: 12,
          borderWidth: 1,
          borderColor: "#111827",
          marginRight: 8,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked ? <View style={{ width: 7, height: 7, backgroundColor: "#16a34a" }} /> : null}
      </View>
      <Text style={{ fontSize: 11 }}>{label}</Text>
    </View>
  );
}

function Underlined({ value, minChars = 12 }) {
  const v = safe(value).trim();
  return (
    <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted", fontSize: 11 }}>
      {v ? v : " ".repeat(minChars)}
    </Text>
  );
}

function Line({ label, value }) {
  const v = safe(value).trim();
  return (
    <Text style={{ fontSize: 11, marginBottom: 6 }}>
      <Text style={{ fontWeight: "bold" }}>{label}</Text>{" "}
      <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
        {v ? v : "________________________"}
      </Text>
    </Text>
  );
}

function SignatureLine({ label, signatureSrc }) {
  const safeSig = isSafeImageSrc(signatureSrc) ? signatureSrc : null;

  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Text style={{ fontSize: 11 }}>{label}</Text>

      <View
        style={{
          width: 230,
          height: 48,
          borderBottomWidth: 1,
          borderBottomColor: "#111827",
          borderStyle: "dotted",
          justifyContent: "flex-end",
        }}
      >
        {safeSig ? <Image src={safeSig} style={{ width: "100%", height: 48, objectFit: "contain" }} /> : null}
      </View>
    </View>
  );
}

function Spacer() {
  return (
    <View
      style={{
        marginVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: "#e5e7eb",
      }}
    />
  );
}

const OPERATIUNI = [
  { value: "copiere_cheie_cu_cip", label: "Copiere cheie cu cip" },
  {
    value: "copiere_cheie_cu_loc_pentru_cip_si_mutare_transponder",
    label: "Copiere cheie cu loc pentru cip si mutare transponder",
  },
  { value: "schimbare_carcase_auto", label: "Schimbare carcase auto*" },
  { value: "prelucrare_chei_deteriorate", label: "Prelucrare chei deteriorate**" },
  { value: "schimbare_baterie", label: "Schimbare baterie" },
  { value: "mutare_transponder", label: "Mutare Transponder (CIP)***" },
  { value: "prelucrare_cheia_clientului", label: "Prelucrare cheia clientului****" },
  { value: "lipit_butoane_telecomenzi", label: "Lipit butoane telecomenzi" },
  { value: "alta", label: "Alta" },
];

export default function ClientFormFisaServicePdfTemplate({ data, companyInfo }) {
  const employeeName = safe(data?.employeeName);
  const companyName = safe(data?.userLocation?.employer || data?.companyName || companyInfo?.name);
  const locatie = safe(data?.userLocation?.label || data?.userLocation?.name || "");
  const inputDate = safe(data?.inputDate);
  const ora = safe(data?.ora);

  const numeClient = safe(data?.numeClient);
  const clientPhone = safe(data?.clientPhone);
  const productDescription = safe(data?.productDescription);

  const defectChoice = safe(data?.defectChoice || "none");
  const defecteText = safe(data?.defecteText);

  const oper = data?.operatiune || data?.operatiuneValues || [];
  const operAlt = safe(data?.operatiuneAltulText);

  const observatii = safe(data?.observatii);

  const estimVal = safe(data?.estimareDurataValue);
  const estimUnit = safe(data?.estimareDurataUnit?.label || data?.estimareDurataUnit?.value);
  const pret = safe(data?.pretTotalLei);

  const riscuri = safe(data?.riscuri);

  const acknowledged = Boolean(data?.acknowledged);
  const dataSubsemnatul = safe(data?.dataSubsemnatul);
  const dataAngajatul = safe(data?.dataAngajatul);

  const clientSig = data?.clientSignature || data?.semnaturaSubsemnatul || null;
  const employeeSig = data?.employeeSignature || data?.semnaturaAngajatul || null;

  // NEW: receipt number (Actualizare Bon)
  const receipt = safe(data?.receipt);
  const receiptDate = safe(data?.receiptDate);

  const logoSrc = hardcodedLogo;

  return (
    <Document>
      <Page size="A4" style={{ ...base.page, padding: 42 }}>
        {/* Header logo */}
        <View style={{ alignItems: "center", marginBottom: 10 }}>
          <Image
            src={logoSrc}
            style={{
              width: 220,
              height: 120,
              objectFit: "contain",
            }}
          />
        </View>

        {/* Title */}
        <View style={{ alignItems: "center", marginBottom: 10 }}>
          <Text style={{ fontSize: 16, fontWeight: "bold" }}>FIȘĂ SERVICE</Text>
        </View>


        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 11, marginBottom: 4 }}>Conform bonului fiscal se confirmă recepția produsului</Text>
          <Line label="Număr bon:" value={receipt} />
          {receiptDate ? <Line label="Data bon:" value={receiptDate} /> : null}
        </View>

        {/* Intro paragraph */}
        <View style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 11, lineHeight: 1.35 }}>
            Subsemnatul, <Underlined value={employeeName} minChars={18} />, în calitate de angajat al{" "}
            <Underlined value={companyName} minChars={18} /> cu punctul de lucru în incinta{" "}
            <Underlined value={locatie} minChars={18} />, am primit azi <Underlined value={inputDate} minChars={12} />{" "}
            ora <Underlined value={ora} minChars={8} />,
          </Text>

          <Text style={{ fontSize: 11, lineHeight: 1.35, marginTop: 6 }}>
            De la subsemnatul <Underlined value={numeClient} minChars={22} />, în calitate de cumpărător,
            {clientPhone.trim().length > 0 ? (
              <>
                {" nr. de telefon: "}
                <Underlined value={clientPhone} minChars={14} />
              </>
            ) : null}
          </Text>
        </View>

        {/* Product description */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6 }}>Următorul produs:</Text>
        <View style={{ marginBottom: 12 }}>
          <Underlined value={productDescription} minChars={18} />
        </View>

        {/* Product condition */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 8 }}>Produsul</Text>
        <Checkbox checked={defectChoice === "none"} label="Nu prezintă defecte" />
        <Checkbox checked={defectChoice === "has"} label="Prezintă următoarele defecte:" />

        {defectChoice === "has" ? (
          <View style={{ marginTop: 6 }}>
            <Underlined value={defecteText || " "} minChars={18} />
          </View>
        ) : null}

        <Spacer />

        {/* Operations */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 8 }}>
          Acestui produs i se va efectua următoarea operațiune:
        </Text>

        {OPERATIUNI.filter((o) => o.value !== "alta").map((o) => (
          <Checkbox key={o.value} checked={hasValue(oper, o.value)} label={o.label} />
        ))}

        <Checkbox checked={altChecked(oper, operAlt)} label={`Alta: ${altChecked(oper, operAlt) ? operAlt : ""}`} />

        <Spacer />

        {/* Observations */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6, textAlign: "center" }}>
          OBSERVATII
        </Text>
        <View>
          <Underlined value={observatii || " "} minChars={0} />
        </View>

        <Text style={{ fontSize: 10.5, marginTop: 8, opacity: 0.9 }}>
          *transfer placuță electronică și cip; **chei rupte/chei uzate; ***transferul cip-ului dintr-o cheie în alta;
          ****gravare/tăiere lamelă;
        </Text>

        <Spacer />

        {/* Estimate & price */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6 }}>Estimare si pret</Text>
        <Text style={{ fontSize: 11, marginBottom: 6 }}>
          Întreaga operațiune va dura{" "}
          <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {estimVal || "______"} {estimUnit || "minute/ore/zile"}
          </Text>
          , iar prețul total va fi de{" "}
          <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {pret || "______"} lei (include TVA)
          </Text>
          .
        </Text>

        <Spacer />

        {/* Risks */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6 }}>
          Riscuri previzibile în urma acestei operațiuni:
        </Text>
        <View>
          <Underlined value={riscuri || " "} minChars={18} />
        </View>

        <Spacer />

        {/* Agreement & signatures */}
        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6 }}>Acord si semnaturi</Text>
        <Checkbox checked={true} label="Am luat la cunostinta riscurile acestei operatiuni." />
        <Checkbox checked={true} label="Prin semnarea prezentului document, sunt de acord ca Silca Centre Profesionale de copiat Chei să colecteze și să prelucreze datele mele cu caracter personal (nume și număr de telefon) exclusiv în scopul prestării serviciului solicitat și pentru notificări legate de starea comenzii. Datele vor fi stocate pe durata prevăzută de legislația fiscală și nu vor fi transmise către terți. Conform Regulamentului (UE) 2016/679, aveți dreptul de acces, rectificare sau ștergere a datelor, contactându-ne la adresa de email specificată în antet." />

        <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 10 }}>
          <View style={{ width: "48%" }}>
            <Text style={{ fontSize: 11, marginBottom: 6 }}>
              Subsemnatul:{" "}
              <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {numeClient || "_____________________"}
              </Text>
            </Text>
            <Text style={{ fontSize: 11, marginBottom: 10 }}>
              Data:{" "}
              <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {dataSubsemnatul || "____/____/______"}
              </Text>
            </Text>
            <SignatureLine label="Semnatura client: " signatureSrc={clientSig} />
          </View>

          <View style={{ width: "48%" }}>
            <Text style={{ fontSize: 11, marginBottom: 6 }}>
              Angajatul:{" "}
              <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {employeeName || "_____________________"}
              </Text>
            </Text>
            <Text style={{ fontSize: 11, marginBottom: 10 }}>
              Data:{" "}
              <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {dataAngajatul || "____/____/______"}
              </Text>
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}